import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fmtIDR, fmtNum, FUEL_META } from "@/lib/format";
import { Fuel, TrendingUp, Wallet, Receipt, Droplet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, Legend } from "recharts";

const KPI = ({ icon: Icon, label, value, sub, accent = "red", testid }) => (
  <Card data-testid={testid} className="border-slate-200 shadow-sm">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
          <div className="mt-2 text-3xl font-extrabold tracking-tight font-mono-fig text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg grid place-items-center ${
          accent === "red" ? "bg-red-50 text-[#E30613]" :
          accent === "blue" ? "bg-blue-50 text-[#005DAA]" :
          accent === "emerald" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-700"
        }`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const RatioCard = ({ label, value, unit, hint, pct, good, testid }) => {
  const display = value == null ? "—" :
    pct ? `${(value * 100).toFixed(1)}${unit}` :
    `${value.toFixed ? value.toFixed(2) : value}${unit}`;
  return (
    <Card data-testid={testid} className={`border ${value == null ? "border-slate-200" : good ? "border-emerald-200 bg-emerald-50/30" : "border-amber-200 bg-amber-50/30"}`}>
      <CardContent className="p-4">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`mt-1 text-xl font-extrabold font-mono-fig ${value == null ? "text-slate-400" : good ? "text-emerald-700" : "text-amber-700"}`}>{display}</div>
        <div className="text-[10px] text-slate-500 mt-1">{hint}</div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [stock, setStock] = useState([]);
  const [fuel30d, setFuel30d] = useState([]);
  const [ratios, setRatios] = useState(null);
  const [metric, setMetric] = useState("revenue"); // revenue | liters | margin

  useEffect(() => {
    api.get("/reports/dashboard").then(r => setData(r.data));
    api.get("/stock").then(r => setStock(r.data));
    api.get("/reports/sales-by-fuel-30d").then(r => setFuel30d(r.data));
    api.get("/reports/ratios").then(r => setRatios(r.data));
  }, []);

  if (!data) return <div className="text-slate-500">Memuat dashboard...</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Dashboard SPBU</h1>
        <p className="text-sm text-slate-500 mt-1">Ringkasan keuangan & operasional hari ini</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Receipt} label="Penjualan Hari Ini" value={fmtIDR(data.today.sales_total)} sub={`${fmtNum(data.today.sales_liters)} L • ${data.today.tx_count} tx`} accent="red" testid="kpi-today-sales" />
        <KPI icon={TrendingUp} label="Pendapatan Bulan Ini" value={fmtIDR(data.month.revenue)} accent="blue" testid="kpi-month-revenue" />
        <KPI icon={Wallet} label="Laba Kotor Bulan Ini" value={fmtIDR(data.month.gross_profit)} sub={`HPP ${fmtIDR(data.month.hpp)}`} accent="emerald" testid="kpi-month-gross" />
        <KPI icon={Fuel} label="Laba Bersih Estimasi" value={fmtIDR(data.month.net_profit)} sub={`Beban ${fmtIDR(data.month.expenses)}`} accent="red" testid="kpi-month-net" />
      </div>

      {ratios && (
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xl font-bold tracking-tight text-slate-900">Rasio Keuangan (YTD)</h2>
            <span className="text-xs text-slate-500">Per {ratios.as_of}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <RatioCard label="Current Ratio" value={ratios.ratios.current_ratio} unit="x" hint="≥ 1,5 sehat" good={ratios.ratios.current_ratio >= 1.5} testid="ratio-current" />
            <RatioCard label="Quick Ratio" value={ratios.ratios.quick_ratio} unit="x" hint="≥ 1 sehat" good={ratios.ratios.quick_ratio >= 1} testid="ratio-quick" />
            <RatioCard label="Cash Ratio" value={ratios.ratios.cash_ratio} unit="x" hint="≥ 0,5 aman" good={ratios.ratios.cash_ratio >= 0.5} testid="ratio-cash" />
            <RatioCard label="Gross Margin" value={ratios.ratios.gross_margin_pct} unit="%" pct hint="Margin BBM" good={ratios.ratios.gross_margin_pct >= 0.05} testid="ratio-gm" />
            <RatioCard label="ROA" value={ratios.ratios.roa} unit="%" pct hint="Return on Assets" good={ratios.ratios.roa >= 0.05} testid="ratio-roa" />
            <RatioCard label="DIO" value={ratios.ratios.days_inventory_outstanding} unit=" hari" hint="Days Inventory" good={ratios.ratios.days_inventory_outstanding && ratios.ratios.days_inventory_outstanding <= 14} inverse testid="ratio-dio" />
          </div>
        </div>
      )}

      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-bold tracking-tight">Tren 30 Hari per Jenis BBM</CardTitle>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
            {[["revenue","Revenue"],["liters","Liter"],["margin","Margin"]].map(([k,l]) => (
              <button key={k} onClick={() => setMetric(k)} data-testid={`chart-metric-${k}`}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${metric === k ? "bg-white shadow text-slate-900" : "text-slate-600"}`}>
                {l}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={fuel30d}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tickFormatter={(v) => metric === "liters" ? `${v}L` : `${(v/1e6).toFixed(1)}Jt`} tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip formatter={(v) => metric === "liters" ? `${fmtNum(v)} L` : fmtIDR(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {Object.entries(FUEL_META).map(([code, m]) => {
                const suffix = metric === "revenue" ? "" : `_${metric === "liters" ? "L" : "margin"}`;
                return <Line key={code} type="monotone" dataKey={`${code}${suffix}`} name={m.name} stroke={
                  code === "pertalite" ? "#10B981" : code === "dexlite" ? "#84CC16" : code === "pertamax" ? "#2563EB" : "#E11D48"
                } strokeWidth={2} dot={false} />;
              })}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-bold tracking-tight">Penjualan 7 Hari Terakhir</CardTitle>
        </CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.chart_7d}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 12, fill: "#64748B" }} />
              <YAxis tickFormatter={(v) => `${(v/1e6).toFixed(1)}Jt`} tick={{ fontSize: 12, fill: "#64748B" }} />
              <Tooltip formatter={(v) => fmtIDR(v)} />
              <Bar dataKey="total" fill="#E30613" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Level Stock BBM</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stock.map((s) => {
            const meta = FUEL_META[s.code];
            return (
              <Card key={s.code} className={`${meta.border} border-2`} data-testid={`stock-card-${s.code}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                    <div className={`text-sm font-bold ${meta.text}`}>{meta.name}</div>
                  </div>
                  <div className="text-3xl font-extrabold font-mono-fig text-slate-900">
                    {fmtNum(s.stock_liters, 0)}<span className="text-base font-semibold text-slate-500 ml-1">L</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span>Nilai: {fmtIDR(s.stock_value)}</span>
                    <Droplet className={`w-4 h-4 ${meta.text}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
